import { useIntersectionObserver, useResizeObserver } from '@vueuse/core';
import debounce from 'lodash-es/debounce';
import { computed, nextTick, onBeforeUnmount, onDeactivated, onMounted, onUpdated, type Ref, ref } from 'vue';

import { DEBOUNCE } from '../../constants';
import { toElement } from '../../utils/templateRefNarrowing';
import { DATA_ATTRIBUTES } from './constants';
import { useOverflowCalculator } from './useOverflowCalculator';
import {
  addActionIdsToFirstLevelChildren as addActionIds,
  cleanupOverflowIds,
  createElementIdMap,
  syncMoreActionsVisibility,
} from './utils';

interface CreateIntersectionObserverOptions {
  actionsContainerRef: Ref<unknown>;
  overflowIds: Ref<Set<string>>;
  moreDropdownMenuRef: Ref<unknown>;
  trackerElementRef: Ref<unknown>;
  moreDropdownWidth: Ref<number>;
  calculateMoreButtonWidth: () => number;
  hasMoreActionsSlot: Ref<boolean>;
  onOverflowChange?: () => void;
}

export function createIntersectionObserver({
  actionsContainerRef,
  overflowIds,
  moreDropdownMenuRef,
  trackerElementRef,
  moreDropdownWidth,
  calculateMoreButtonWidth,
  hasMoreActionsSlot,
  onOverflowChange,
}: CreateIntersectionObserverOptions) {
  const isInitializing = ref(false);

  const {
    calculateOverflow,
    applyElementVisibility,
    updateOverflowState,
    updateTrackerBounding,
    cleanup: cleanupCalculator,
    isProcessing,
  } = useOverflowCalculator({
    actionsContainerRef,
    overflowIds,
    moreDropdownMenuRef,
    trackerElementRef,
    onOverflowChange,
  });

  let mutationObserver: MutationObserver | null = null;
  let stopResizeObserver: (() => void) | null = null;
  let stopIntersectionObserver: (() => void) | null = null;

  function clearOverflowState() {
    overflowIds.value.clear();
    moreDropdownWidth.value = 0;
  }

  /**
   * Cleanup all observers (ResizeObserver and IntersectionObserver)
   * to prevent memory leaks when re-creating them
   */
  function cleanupObservers() {
    if (stopResizeObserver) {
      stopResizeObserver();
      stopResizeObserver = null;
    }
    if (stopIntersectionObserver) {
      stopIntersectionObserver();
      stopIntersectionObserver = null;
    }
  }

  const actionsContainerEl = computed(() => toElement(actionsContainerRef.value));

  // Track DOM mutations - created once, outside of createObserver to prevent duplicates
  const handleMutation = debounce(
    () => {
      const container = toElement(actionsContainerRef.value);
      if (!container || !hasMoreActionsSlot.value) return;

      // Add data-action-id to first-level children
      addActionIds(container);

      // Cleanup old observers before re-initializing
      cleanupObservers();
      cleanupCalculator();
      debouncedInitObserve();
    },
    DEBOUNCE.FAST,
    { leading: false, trailing: true },
  );

  function createObserver() {
    const container = toElement(actionsContainerRef.value);
    if (!container) return;

    // If there's no more-actions slot, don't set up overflow detection
    if (!hasMoreActionsSlot.value) return;

    // Get all direct children of the actions container
    const directChildren = Array.from(container.children) as HTMLElement[];

    // Create a map of elements to their IDs (either data-action-id or index-based)
    const elementIdMap = createElementIdMap(directChildren);

    // Clean up overflowIds for elements that no longer exist
    const currentElementIds = new Set(elementIdMap.values());
    cleanupOverflowIds(overflowIds.value, currentElementIds);

    // to track container size changes
    const { stop: stopResize } = useResizeObserver(actionsContainerEl, () => {
      updateTrackerBounding();
      calculateOverflow();
    });
    stopResizeObserver = stopResize;

    const { stop: stopIntersection } = useIntersectionObserver(
      directChildren,
      (entries) => {
        if (isProcessing.value) {
          return;
        }

        let hasChanges = false;
        const newOverflowIds = new Set(overflowIds.value);

        entries.forEach((entry) => {
          const element = entry.target as HTMLElement;
          const elementId = elementIdMap.get(element) || element.getAttribute(DATA_ATTRIBUTES.ACTION_ID) || undefined;

          // Check if element is fully visible
          if (entry.intersectionRatio === 1) {
            // show action, hide in dropdown
            if (elementId && newOverflowIds.has(elementId)) {
              newOverflowIds.delete(elementId);
              hasChanges = true;
            }
          } else {
            // hide action, show in dropdown (intersectionRatio < 1 means partially or not visible)
            if (elementId && !newOverflowIds.has(elementId)) {
              newOverflowIds.add(elementId);
              hasChanges = true;
            }
          }
        });

        // Only update if there are actual changes
        if (hasChanges) {
          const currentOverflowState = Array.from(overflowIds.value).sort().join(',');
          const newOverflowState = Array.from(newOverflowIds).sort().join(',');

          if (currentOverflowState !== newOverflowState) {
            // Update overflow state and apply visibility
            updateOverflowState(newOverflowIds, directChildren, elementIdMap);

            // Sync more-actions content visibility
            syncMoreActionsVisibility(toElement(moreDropdownMenuRef.value), overflowIds.value);

            // Call callback if provided and state actually changed
            // Use nextTick to ensure CSS changes are applied before calculating visible elements width
            if (onOverflowChange) {
              nextTick(() => {
                onOverflowChange();
              });
            }
          }
        }
      },
      { root: container },
    );
    stopIntersectionObserver = stopIntersection;

    // Apply initial visibility to all elements
    directChildren.forEach((element) => {
      const elementId = elementIdMap.get(element) || element.getAttribute(DATA_ATTRIBUTES.ACTION_ID) || '';
      applyElementVisibility(element, elementId, overflowIds.value);
    });

    // Initial calculation
    calculateOverflow();

    syncMoreActionsVisibility(toElement(moreDropdownMenuRef.value), overflowIds.value);
  }

  function initObserve() {
    const container = toElement(actionsContainerRef.value);
    if (!container) return;

    // If there's no more-actions slot, don't initialize
    if (!hasMoreActionsSlot.value) return;

    // Add data-action-id to first-level children
    addActionIds(container);

    createObserver();

    // Set initial dropdown width - only if not already set
    if (moreDropdownWidth.value === 0) {
      moreDropdownWidth.value = calculateMoreButtonWidth();
    }

    // Setup MutationObserver once to watch for DOM changes; only create if it doesn't exist to prevent duplicates
    if (!mutationObserver) {
      mutationObserver = new MutationObserver(handleMutation);
      mutationObserver.observe(container, {
        childList: true, // Watch for added/removed children
        subtree: false, // Only watch direct children
      });
    }
  }

  const debouncedInitObserve = debounce(
    () => {
      if (!isInitializing.value) {
        isInitializing.value = true;
        initObserve();
        isInitializing.value = false;
      }
    },
    DEBOUNCE.FAST,
    { leading: true },
  );

  onMounted(() => {
    // If there's no more-actions slot, skip initialization
    if (!hasMoreActionsSlot.value) return;

    // Add data-action-id to first-level children
    addActionIds(toElement(actionsContainerRef.value));
    initObserve();
  });

  onUpdated(() => {
    // If there's no more-actions slot, skip initialization
    if (!hasMoreActionsSlot.value) return;

    const container = toElement(actionsContainerRef.value);
    // Add data-action-id to first-level children
    addActionIds(container);
    // Clear overflowIds if no actions are present
    if (container) {
      const directChildren = Array.from(container.children) as HTMLElement[];
      if (directChildren.length === 0) clearOverflowState();
    }
    cleanupObservers();
    cleanupCalculator();
    debouncedInitObserve();
  });

  onDeactivated(() => {
    cleanupObservers();
    cleanupCalculator();
    handleMutation.cancel();
    debouncedInitObserve.cancel();
    if (mutationObserver) {
      mutationObserver.disconnect();
      mutationObserver = null;
    }
  });

  onBeforeUnmount(() => {
    cleanupObservers();
    cleanupCalculator();
    handleMutation.cancel();
    debouncedInitObserve.cancel();
    if (mutationObserver) {
      mutationObserver.disconnect();
      mutationObserver = null;
    }
  });
}
